import { useState, useEffect } from 'react';
import api from './api';
import './AccountManagement.css';
import './FixAirwayManagement.css';

const EMPTY_FIX = { fixId: '', lat: '', lng: '' };

export default function FixAirwayManagement() {
  const [fixList,    setFixList]    = useState([]);
  const [vtpList,    setVtpList]    = useState([]);
  const [airwayList, setAirwayList] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [msg,        setMsg]        = useState({ text: '', type: '' });

  // FIX 추가
  const [addingFix,  setAddingFix]  = useState(false);
  const [newFix,     setNewFix]     = useState(EMPTY_FIX);
  const [savingFix,  setSavingFix]  = useState(false);

  // FIX 수정
  const [editFixUid,   setEditFixUid]   = useState(null);
  const [editFix,      setEditFix]      = useState(EMPTY_FIX);
  const [updatingFix,  setUpdatingFix]  = useState(false);

  // FIX 삭제 확인 모달
  const [fixDelModal, setFixDelModal] = useState(null); // { fix, connectedAirways }

  // Airway 수정
  const [editAirwayUid,   setEditAirwayUid]   = useState(null);
  const [editAirway,      setEditAirway]       = useState({ name: '', groupName: '' });
  const [updatingAirway,  setUpdatingAirway]   = useState(false);

  // Airway 생성 폼
  const [airwayName, setAirwayName] = useState('');
  const [groupName,  setGroupName]  = useState('');
  const [fromType,   setFromType]   = useState('vertiport');
  const [fromUid,    setFromUid]    = useState('');
  const [toType,     setToType]     = useState('vertiport');
  const [toUid,      setToUid]      = useState('');
  const [creating,   setCreating]   = useState(false);

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    try {
      const [f, v, a] = await Promise.all([
        api.get('/api/fix/list'),
        api.get('/api/vertiport/list'),
        api.get('/api/airway/list'),
      ]);
      setFixList(f.data);
      setVtpList(v.data);
      setAirwayList(a.data);
    } catch {
      setMsg({ text: '데이터를 불러오지 못했습니다.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  async function createFix() {
    const fixId = newFix.fixId.trim();
    const lat   = parseFloat(newFix.lat);
    const lng   = parseFloat(newFix.lng);
    if (!fixId || isNaN(lat) || isNaN(lng)) {
      setMsg({ text: 'FIX ID, 위도, 경도를 모두 입력하세요.', type: 'error' }); return;
    }
    setSavingFix(true);
    setMsg({ text: '', type: '' });
    try {
      await api.post('/api/fix/create', { fixId, lat, lng });
      const [f, a] = await Promise.all([api.get('/api/fix/list'), api.get('/api/airway/list')]);
      setFixList(f.data);
      setAirwayList(a.data);
      setNewFix(EMPTY_FIX);
      setAddingFix(false);
      setMsg({ text: 'FIX가 등록되었습니다.', type: 'success' });
    } catch (e) {
      setMsg({ text: e.response?.data?.message || 'FIX 등록 실패', type: 'error' });
    } finally {
      setSavingFix(false);
    }
  }

  function startEdit(fix) {
    setEditFixUid(fix.fixUid);
    setEditFix({ fixId: fix.fixId, lat: String(fix.lat), lng: String(fix.lng) });
    setMsg({ text: '', type: '' });
  }

  function cancelEdit() {
    setEditFixUid(null);
    setEditFix(EMPTY_FIX);
  }

  async function updateFix() {
    const fixId = editFix.fixId.trim();
    if (!fixId) { setMsg({ text: 'FIX ID를 입력하세요.', type: 'error' }); return; }
    setUpdatingFix(true);
    setMsg({ text: '', type: '' });
    try {
      const body = { fixId };
      const lat = parseFloat(editFix.lat);
      const lng = parseFloat(editFix.lng);
      if (!isNaN(lat)) body.lat = lat;
      if (!isNaN(lng)) body.lng = lng;
      await api.put(`/api/fix/${editFixUid}`, body);
      const f = await api.get('/api/fix/list');
      setFixList(f.data);
      setEditFixUid(null);
      setMsg({ text: 'FIX가 수정되었습니다.', type: 'success' });
    } catch (e) {
      setMsg({ text: e.response?.data?.message || 'FIX 수정 실패', type: 'error' });
    } finally {
      setUpdatingFix(false);
    }
  }

  async function deleteFix(fixUid) {
    try {
      await api.delete(`/api/fix/${fixUid}`);
      const [f, a] = await Promise.all([api.get('/api/fix/list'), api.get('/api/airway/list')]);
      setFixList(f.data);
      setAirwayList(a.data);
      setFixDelModal(null);
      setMsg({ text: 'FIX가 삭제되었습니다.', type: 'success' });
    } catch (e) {
      setFixDelModal(null);
      setMsg({ text: e.response?.data?.message || 'FIX 삭제 실패', type: 'error' });
    }
  }

  async function deleteAirway(airwayUid) {
    try {
      await api.delete(`/api/airway/${airwayUid}`);
      const a = await api.get('/api/airway/list');
      setAirwayList(a.data);
      setMsg({ text: 'Airway가 삭제되었습니다.', type: 'success' });
    } catch (e) {
      setMsg({ text: e.response?.data?.message || 'Airway 삭제 실패', type: 'error' });
    }
  }

  function startEditAirway(aw) {
    setEditAirwayUid(aw.airwayUid);
    setEditAirway({ name: aw.name, groupName: aw.groupName || '' });
    setMsg({ text: '', type: '' });
  }

  function cancelEditAirway() {
    setEditAirwayUid(null);
    setEditAirway({ name: '', groupName: '' });
  }

  async function updateAirway() {
    const name = editAirway.name.trim();
    if (!name) { setMsg({ text: 'Airway 이름을 입력하세요.', type: 'error' }); return; }
    setUpdatingAirway(true);
    setMsg({ text: '', type: '' });
    try {
      await api.put(`/api/airway/${editAirwayUid}`, {
        name,
        groupName: editAirway.groupName.trim() || null,
      });
      const a = await api.get('/api/airway/list');
      setAirwayList(a.data);
      setEditAirwayUid(null);
      setMsg({ text: 'Airway가 수정되었습니다.', type: 'success' });
    } catch (e) {
      setMsg({ text: e.response?.data?.message || 'Airway 수정 실패', type: 'error' });
    } finally {
      setUpdatingAirway(false);
    }
  }

  async function createAirway() {
    if (!airwayName.trim()) { setMsg({ text: 'Airway 이름을 입력하세요.', type: 'error' }); return; }
    if (!fromUid || !toUid) { setMsg({ text: '출발·도착 노드를 선택하세요.', type: 'error' }); return; }
    if (fromType === toType && Number(fromUid) === Number(toUid)) {
      setMsg({ text: '같은 노드는 연결할 수 없습니다.', type: 'error' }); return;
    }
    setCreating(true);
    setMsg({ text: '', type: '' });
    try {
      await api.post('/api/airway/create', {
        name:      airwayName.trim(),
        groupName: groupName.trim() || null,
        fromType,  fromUid: Number(fromUid),
        toType,    toUid:   Number(toUid),
      });
      const a = await api.get('/api/airway/list');
      setAirwayList(a.data);
      setAirwayName('');
      setFromUid('');
      setToUid('');
      setMsg({ text: 'Airway가 생성되었습니다.', type: 'success' });
    } catch (e) {
      setMsg({ text: e.response?.data?.message || 'Airway 생성 실패', type: 'error' });
    } finally {
      setCreating(false);
    }
  }

  function nodeOptions(type) {
    return type === 'fix'
      ? fixList.map(f => ({ uid: f.fixUid, label: f.fixId }))
      : vtpList.map(v => ({ uid: v.vertiportUid, label: `${v.vertiportId} — ${v.name}` }));
  }

  // Airway 목록을 groupName별로 묶기
  const airwayGroups = (() => {
    const groups = new Map(); // groupName(or '') → airway[]
    airwayList.forEach(aw => {
      const key = aw.groupName || '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(aw);
    });
    return groups;
  })();

  const existingGroups = [...new Set(airwayList.map(aw => aw.groupName).filter(Boolean))];

  if (loading) return <div className="acm-placeholder">불러오는 중...</div>;

  return (
    <div className="fam">
      {msg.text && (
        <p className={`acm-msg acm-msg-${msg.type} fam-msg`}>{msg.text}</p>
      )}

      <div className="fam-cols">
        {/* ── FIX 목록 ── */}
        <div className="fam-panel">
          <div className="fam-panel-hd">
            <span>FIX 포인트 <span className="fam-count">({fixList.length})</span></span>
            <button
              className="fam-add-btn"
              onClick={() => { setAddingFix(v => !v); setNewFix(EMPTY_FIX); setMsg({ text: '', type: '' }); }}
            >
              {addingFix ? '✕ 취소' : '+ FIX 추가'}
            </button>
          </div>

          {/* FIX 추가 폼 */}
          {addingFix && (
            <div className="fam-create">
              <div className="fam-create-hd">FIX 등록</div>
              <input
                className="acm-input"
                placeholder="FIX ID (예: ABCDE)"
                value={newFix.fixId}
                onChange={e => setNewFix(p => ({ ...p, fixId: e.target.value }))}
              />
              <div className="fam-create-row">
                <input
                  className="acm-input"
                  placeholder="위도 (lat)"
                  value={newFix.lat}
                  onChange={e => setNewFix(p => ({ ...p, lat: e.target.value }))}
                />
                <input
                  className="acm-input"
                  placeholder="경도 (lng)"
                  value={newFix.lng}
                  onChange={e => setNewFix(p => ({ ...p, lng: e.target.value }))}
                />
              </div>
              <button className="acm-save-btn fam-create-btn" onClick={createFix} disabled={savingFix}>
                {savingFix ? '등록 중...' : '등록'}
              </button>
            </div>
          )}

          <div className="fam-list">
            {fixList.length === 0 ? (
              <div className="acm-placeholder">등록된 FIX가 없습니다.</div>
            ) : fixList.map(fix => (
              <div key={fix.fixUid} className="fam-item fam-item-col">
                {editFixUid === fix.fixUid ? (
                  /* ── 수정 폼 ── */
                  <div className="fam-edit-form">
                    <input
                      className="acm-input fam-edit-input"
                      placeholder="FIX ID"
                      value={editFix.fixId}
                      onChange={e => setEditFix(p => ({ ...p, fixId: e.target.value }))}
                    />
                    <div className="fam-create-row">
                      <input
                        className="acm-input"
                        placeholder="위도"
                        value={editFix.lat}
                        onChange={e => setEditFix(p => ({ ...p, lat: e.target.value }))}
                      />
                      <input
                        className="acm-input"
                        placeholder="경도"
                        value={editFix.lng}
                        onChange={e => setEditFix(p => ({ ...p, lng: e.target.value }))}
                      />
                    </div>
                    <div className="fam-edit-btns">
                      <button className="acm-save-btn fam-edit-save" onClick={updateFix} disabled={updatingFix}>
                        {updatingFix ? '저장 중...' : '저장'}
                      </button>
                      <button className="fam-del-btn" onClick={cancelEdit}>취소</button>
                    </div>
                  </div>
                ) : (
                  /* ── 일반 표시 ── */
                  <>
                    <div className="fam-item-info">
                      <span className="fam-item-id">{fix.fixId}</span>
                      <span className="fam-item-coord">{Number(fix.lat).toFixed(5)}, {Number(fix.lng).toFixed(5)}</span>
                    </div>
                    <div className="fam-item-actions">
                      <button className="fam-edit-btn" onClick={() => startEdit(fix)}>수정</button>
                      <button className="fam-del-btn" onClick={() => {
                        const connected = airwayList.filter(aw =>
                          (aw.from.type === 'fix' && aw.from.uid === fix.fixUid) ||
                          (aw.to.type === 'fix' && aw.to.uid === fix.fixUid)
                        );
                        setFixDelModal({ fix, connectedAirways: connected });
                      }}>삭제</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Airway 목록 + 생성 ── */}
        <div className="fam-panel fam-panel-right">
          <div className="fam-panel-hd">
            <span>Airway <span className="fam-count">({airwayList.length})</span></span>
          </div>

          {/* Airway 생성 폼 */}
          <div className="fam-create">
            <div className="fam-create-hd">노선 생성</div>
            <div className="fam-create-row">
              <input
                className="acm-input"
                placeholder="구간명 (Airway 이름)"
                value={airwayName}
                onChange={e => setAirwayName(e.target.value)}
                style={{ flex: 1 }}
              />
              <input
                className="acm-input fam-group-input"
                placeholder="노선 그룹명"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                list="fam-groups"
              />
              <datalist id="fam-groups">
                {existingGroups.map(g => <option key={g} value={g} />)}
              </datalist>
            </div>
            <div className="fam-create-row">
              <select value={fromType} onChange={e => { setFromType(e.target.value); setFromUid(''); }} className="acm-input fam-sel-type">
                <option value="vertiport">버티포트</option>
                <option value="fix">FIX</option>
              </select>
              <select value={fromUid} onChange={e => setFromUid(e.target.value)} className="acm-input fam-sel-node">
                <option value="">출발 선택</option>
                {nodeOptions(fromType).map(o => (
                  <option key={o.uid} value={o.uid}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="fam-create-arrow">↓</div>
            <div className="fam-create-row">
              <select value={toType} onChange={e => { setToType(e.target.value); setToUid(''); }} className="acm-input fam-sel-type">
                <option value="vertiport">버티포트</option>
                <option value="fix">FIX</option>
              </select>
              <select value={toUid} onChange={e => setToUid(e.target.value)} className="acm-input fam-sel-node">
                <option value="">도착 선택</option>
                {nodeOptions(toType).map(o => (
                  <option key={o.uid} value={o.uid}>{o.label}</option>
                ))}
              </select>
            </div>
            <button className="acm-save-btn fam-create-btn" onClick={createAirway} disabled={creating}>
              {creating ? '생성 중...' : '+ Airway 생성'}
            </button>
          </div>

          {/* Airway 목록 — groupName별 섹션 */}
          <div className="fam-list">
            {airwayList.length === 0 ? (
              <div className="acm-placeholder">등록된 Airway가 없습니다.</div>
            ) : [...airwayGroups.entries()].map(([gn, awList]) => (
              <div key={gn || '__none__'} className="fam-group-section">
                <div className="fam-group-hd">
                  {gn
                    ? <span className="fam-group-name">{gn}</span>
                    : <span className="fam-group-name fam-group-none">그룹 없음</span>
                  }
                  <span className="fam-group-count">{awList.length}개</span>
                </div>
                {awList.map(aw => (
                  <div key={aw.airwayUid} className="fam-item fam-item-col fam-item-segment">
                    {editAirwayUid === aw.airwayUid ? (
                      /* ── Airway 수정 폼 ── */
                      <div className="fam-edit-form">
                        <div className="fam-create-row">
                          <input
                            className="acm-input fam-edit-input"
                            placeholder="구간명 (Airway 이름)"
                            value={editAirway.name}
                            onChange={e => setEditAirway(p => ({ ...p, name: e.target.value }))}
                            style={{ flex: 1 }}
                            autoFocus
                          />
                          <input
                            className="acm-input fam-group-input"
                            placeholder="노선 그룹명"
                            value={editAirway.groupName}
                            onChange={e => setEditAirway(p => ({ ...p, groupName: e.target.value }))}
                            list="fam-edit-groups"
                          />
                          <datalist id="fam-edit-groups">
                            {existingGroups.map(g => <option key={g} value={g} />)}
                          </datalist>
                        </div>
                        <div className="fam-edit-btns">
                          <button className="acm-save-btn fam-edit-save" onClick={updateAirway} disabled={updatingAirway}>
                            {updatingAirway ? '저장 중...' : '저장'}
                          </button>
                          <button className="fam-del-btn" onClick={cancelEditAirway}>취소</button>
                        </div>
                      </div>
                    ) : (
                      /* ── 일반 표시 ── */
                      <>
                        <div className="fam-item-info">
                          <span className="fam-item-name">{aw.name}</span>
                          <div className="fam-airway-route">
                            <span className="fam-airway-node">
                              <span className={`fam-node-badge fam-node-${aw.from.type}`}>{aw.from.type === 'fix' ? 'FIX' : 'VTP'}</span>
                              {aw.from.name}
                            </span>
                            <span className="fam-airway-arrow">→</span>
                            <span className="fam-airway-node">
                              <span className={`fam-node-badge fam-node-${aw.to.type}`}>{aw.to.type === 'fix' ? 'FIX' : 'VTP'}</span>
                              {aw.to.name}
                            </span>
                          </div>
                        </div>
                        <div className="fam-item-actions">
                          <button className="fam-edit-btn" onClick={() => startEditAirway(aw)}>수정</button>
                          <button className="fam-del-btn" onClick={() => deleteAirway(aw.airwayUid)}>삭제</button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FIX 삭제 확인 모달 */}
      {fixDelModal && (
        <div className="fam-del-overlay">
          <div className="fam-del-modal">
            <div className="fam-del-modal-hd">FIX 삭제</div>
            <p className="fam-del-modal-msg"><strong>{fixDelModal.fix.fixId}</strong>을(를) 삭제하시겠습니까?</p>
            {fixDelModal.connectedAirways.length > 0 && (
              <>
                <p className="fam-del-modal-warn">⚠ 연결된 Airway {fixDelModal.connectedAirways.length}개도 함께 삭제됩니다.</p>
                <ul className="fam-del-modal-list">
                  {fixDelModal.connectedAirways.map(aw => (
                    <li key={aw.airwayUid} className="fam-del-modal-item">
                      <span className="fam-del-modal-name">{aw.name}</span>
                      <span className="fam-del-modal-nodes">{aw.from.name} → {aw.to.name}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="fam-del-modal-btns">
              <button className="acm-save-btn fam-del-modal-confirm" onClick={() => deleteFix(fixDelModal.fix.fixUid)}>삭제</button>
              <button className="fam-del-btn" style={{ padding: '7px 16px' }} onClick={() => setFixDelModal(null)}>취소</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
